import React, { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Button,
  Card,
  Text,
  Group,
  SimpleGrid,
  ActionIcon,
  Stack,
  MantineProvider,
  TextInput,
  Pagination,
  Box,
} from '@mantine/core';
import { Link } from '@inertiajs/react';
import {
  IconPlus,
  IconWorld,
  IconBrandFacebook,
  IconBrandInstagram,
  IconArrowRight,
  IconSearch,
} from '@tabler/icons-react';

interface Organizer {
  id: number;
  name: string;
  description: string;
  website_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  slug: string;
  created_at: string;
}

interface OrganizerIndexProps {
  organizers: Organizer[];
}

const OrganizerIndex: React.FC<OrganizerIndexProps> = ({ organizers }) => {
  // State for search and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 12; // 3 columns x 3 rows

  // Filter and sort organizers
  const filteredAndSortedOrganizers = useMemo(() => {
    return [...organizers]
      .filter(
        (organizer) =>
          organizer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (organizer.description && organizer.description.toLowerCase().includes(searchQuery.toLowerCase())),
      )
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [organizers, searchQuery]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedOrganizers.length / ITEMS_PER_PAGE);

  // Get current page items
  const currentOrganizers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedOrganizers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredAndSortedOrganizers, currentPage]);

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <MantineProvider>
      <Container size="lg" py="xl">
        <Title order={1} mb="md" className="poppins" style={{ color: '#f2f2f2' }}>
          Organizers
        </Title>

        <Group mb="lg" align="flex-end">
          <TextInput
            placeholder="Search organizers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            style={{ flexGrow: 1 }}
            styles={{
              input: {
                backgroundColor: '#2c2c2c',
                color: '#f2f2f2',
                borderColor: '#EA580C',
              },
              label: {
                color: '#f2f2f2',
              },
            }}
            className="poppins"
          />

          <Button
            component={Link}
            href="/organizers/suggest"
            leftSection={<IconPlus size={16} />}
            color="#EA580C"
            className="poppins"
          >
            Add New Organizer
          </Button>
        </Group>

        {totalPages > 1 && (
          <Box mt="xl" style={{ display: 'flex', justifyContent: 'left' }}>
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
              color="orange"
              className="poppins"
            />
          </Box>
        )}

        <Text size="sm" mt="md" style={{ color: '#f2f2f2' }} className="poppins mb-4" align="left">
          Showing {currentOrganizers.length} of {filteredAndSortedOrganizers.length} organizers
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
          {currentOrganizers.map((organizer) => (
            <Card
              key={organizer.id}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              h="100%"
              style={{
                backgroundColor: '#3d3c3e',
                borderColor: '#5c5c5c',
              }}
            >
              <Stack h="100%" justify="space-between">
                <div>
                  <Title order={4} mb="xs" className="poppins" style={{ color: '#f2f2f2' }}>
                    <Link
                      href={`/o/${organizer.slug}`}
                      style={{
                        color: '#f2f2f2',
                        textDecoration: 'none',
                      }}
                    >
                      {organizer.name}
                    </Link>
                  </Title>

                  <Text size="sm" className="poppins" lineClamp={3} mb="md" style={{ color: '#d1d1d1' }}>
                    {organizer.description}
                  </Text>
                </div>

                <div>
                  <Group gap="xs">
                    {organizer.website_url && (
                      <ActionIcon
                        component="a"
                        href={organizer.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="#EA580C"
                        size="lg"
                        aria-label="Website"
                      >
                        <IconWorld size={20} />
                      </ActionIcon>
                    )}

                    {organizer.facebook_url && (
                      <ActionIcon
                        component="a"
                        href={organizer.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="#EA580C"
                        size="lg"
                        aria-label="Facebook"
                      >
                        <IconBrandFacebook size={20} />
                      </ActionIcon>
                    )}

                    {organizer.instagram_url && (
                      <ActionIcon
                        component="a"
                        href={organizer.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="subtle"
                        color="#EA580C"
                        size="lg"
                        aria-label="Instagram"
                      >
                        <IconBrandInstagram size={20} />
                      </ActionIcon>
                    )}

                    <ActionIcon
                      component={Link}
                      href={`/o/${organizer.slug}`}
                      variant="filled"
                      color="#EA580C"
                      size="lg"
                      ml="auto"
                      aria-label="View Organizer"
                    >
                      <IconArrowRight size={20} />
                    </ActionIcon>
                  </Group>
                </div>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        {totalPages > 1 && (
          <Box mt="xl" style={{ display: 'flex', justifyContent: 'center' }}>
            <Pagination
              total={totalPages}
              value={currentPage}
              onChange={setCurrentPage}
              color="orange"
              className="poppins"
            />
          </Box>
        )}

        <Text size="sm" mt="md" style={{ color: '#f2f2f2' }} className="poppins" align="center">
          Showing {currentOrganizers.length} of {filteredAndSortedOrganizers.length} organizers
        </Text>
      </Container>
    </MantineProvider>
  );
};

export default OrganizerIndex;
